// Design agent: Consolidates overall network summary into a single, modern result card.
// Developer agent: Combines logic from Ipv4InsightSummary and RouteSummarizer for a unified display.
import React, { useEffect, useMemo, useState } from 'react';
import {
  doNetworksOverlap,
  getTotalAddressCount,
  intToIpv4,
  coveringPrefix,
  summaryWithout,
  findOverlaps,
} from '../utils/ipMath';
import './OverallSummaryCard.css';

// Design agent: The gauge never lets the user summarise wider than /8 — beyond that point the
// route summary would claim so much unrelated address space that it stops being a useful
// shorthand for routing tables. /8 is a reasonable, conservative floor for a "how far could I
// widen this" exploration tool.
// Developer agent: Kept as a module constant so both the slider bounds and the reset button
// reference the exact same value.
const WIDEST_SELECTABLE_PREFIX = 8;

function OverallSummaryCard({ analyses, onHighlightEntries, onClearHighlightedEntries }) {
  // Developer agent: Filter out invalid or empty analyses to only process valid networks.
  const validAnalyses = useMemo(() => {
    return analyses.filter(analysis => analysis.parsed);
  }, [analyses]);

  // Design agent: Keeps each valid entry paired with its original position in the visible entry
  // list, since the impact list below needs to talk about "Entry #N" using the same numbering
  // the user sees on the right-hand side, not a re-packed index.
  // Developer agent: Filtering analyses (see validAnalyses above) loses the original index, so
  // this recomputes the pairing once and is reused wherever that original index matters.
  const indexedValidEntries = useMemo(() => {
    return analyses
      .map((analysis, index) => ({ analysis, index }))
      .filter((entry) => entry.analysis.parsed);
  }, [analyses]);

  // --- Calculations from Ipv4InsightSummary ---
  const totalOverlappingNetworks = useMemo(() => {
    let count = 0;
    for (let i = 0; i < validAnalyses.length; i += 1) {
      for (let j = i + 1; j < validAnalyses.length; j += 1) {
        if (doNetworksOverlap(validAnalyses[i].parsed, validAnalyses[j].parsed)) {
          count += 1;
        }
      }
    }
    return count;
  }, [validAnalyses]);

  const { totalOverlappingHosts, totalUniqueHostsAcrossNetworks } = useMemo(() => {
    const events = [];
    validAnalyses.forEach(analysis => {
      events.push({ type: 'start', ip: analysis.parsed.network });
      events.push({ type: 'end', ip: analysis.parsed.broadcast + 1 });
    });
    events.sort((a, b) => a.ip - b.ip);
    let coverageCount = 0;
    let totalOverlapping = 0;
    let totalUnique = 0;
    let lastIp = null;
    for (const event of events) {
      if (lastIp !== null && event.ip > lastIp) {
        const segmentLength = event.ip - lastIp;
        if (coverageCount > 0) totalUnique += segmentLength;
        if (coverageCount >= 2) totalOverlapping += segmentLength;
      }
      coverageCount += event.type === 'start' ? 1 : -1;
      lastIp = event.ip;
    }
    return { totalOverlappingHosts: totalOverlapping, totalUniqueHostsAcrossNetworks: totalUnique };
  }, [validAnalyses]);

  const totalSizeOfAllNetworks = useMemo(() => {
    return validAnalyses.reduce((sum, analysis) => sum + getTotalAddressCount(analysis.parsed.prefix), 0);
  }, [validAnalyses]);

  const { unmentionedNetworksCount, unmentionedHostsCount } = useMemo(() => {
    const sortedNetworks = [...validAnalyses].sort((a, b) => a.parsed.network - b.parsed.network);
    let unmentionedNets = 0;
    let unmentionedHosts = 0;
    for (let i = 0; i < sortedNetworks.length - 1; i += 1) {
      const currentNetwork = sortedNetworks[i].parsed;
      const nextNetwork = sortedNetworks[i + 1].parsed;
      if (nextNetwork.network > currentNetwork.broadcast + 1) {
        const gapStart = currentNetwork.broadcast + 1;
        const gapEnd = nextNetwork.network - 1;
        const gapSize = gapEnd - gapStart + 1;
        unmentionedHosts += gapSize;
        unmentionedNets += gapSize;
      }
    }
    return { unmentionedNetworksCount: unmentionedNets, unmentionedHostsCount: unmentionedHosts };
  }, [validAnalyses]);

  // --- Spec 004: Route summary explorer -------------------------------------------------------

  // Design agent: The list of parsed networks backing every "what if this entry were removed"
  // calculation below — kept in the same order as indexedValidEntries so positions line up.
  // Developer agent: Memoised separately from indexedValidEntries so summaryWithout receives a
  // plain array of parsed networks, matching the shape coveringPrefix/summaryWithout expect.
  const validParsedList = useMemo(() => {
    return indexedValidEntries.map((entry) => entry.analysis.parsed);
  }, [indexedValidEntries]);

  // Design agent: The tightest possible route summary — the gauge's default and its rightmost
  // (tightest) position. Recomputed live so it always reflects the current set of entries.
  // Developer agent: Delegates the actual bit-math to coveringPrefix, kept and tested in ipMath.js.
  const tightestSummary = useMemo(() => coveringPrefix(validParsedList), [validParsedList]);
  const tightestPrefix = tightestSummary ? tightestSummary.prefix : null;

  // Design agent: The gauge only ever widens the summary, so its floor must never sit tighter
  // than the tightest prefix itself — clamped defensively for the (rare) case where the tightest
  // covering prefix is already wider than /8 (e.g. very spread out entries).
  // Developer agent: Math.min keeps `min <= max` true for the native <input type="range">, which
  // would otherwise silently misbehave if min ended up greater than max.
  const gaugeFloorPrefix = tightestPrefix === null ? WIDEST_SELECTABLE_PREFIX : Math.min(WIDEST_SELECTABLE_PREFIX, tightestPrefix);

  // Design agent: Tracks the user's chosen widened prefix; always starts — and can be reset back
  // — at the tightest covering prefix per spec 004 item 1.
  // Developer agent: useState is seeded once, then kept in sync with tightestPrefix through the
  // effect below whenever the underlying entries change (add/remove/edit).
  const [selectedPrefix, setSelectedPrefix] = useState(tightestPrefix ?? WIDEST_SELECTABLE_PREFIX);

  useEffect(() => {
    if (tightestPrefix !== null) {
      setSelectedPrefix(tightestPrefix);
    }
  }, [tightestPrefix]);

  // Design agent: Resets the gauge back to its tightest (minimal covering) position on demand.
  const handleResetToTightest = () => {
    if (tightestPrefix !== null) {
      setSelectedPrefix(tightestPrefix);
    }
  };

  // Design agent: Derives the widened supernet shown under the gauge from the user's slider
  // position, alongside its total address count and the "headroom" left unclaimed by any entry.
  // Developer agent: Reuses the already-masked tightest network — masking it again with the wider
  // (shorter) selected prefix yields the same address a from-scratch computation would, since
  // prefix masks are nested (a shorter mask is always a subset of a longer one's leading bits).
  const routeExplorer = useMemo(() => {
    if (!tightestSummary || selectedPrefix === null) {
      return null;
    }
    const selectedMask = selectedPrefix === 0 ? 0 : (~0 << (32 - selectedPrefix)) >>> 0;
    const widenedNetwork = (tightestSummary.network & selectedMask) >>> 0;
    const totalAddresses = getTotalAddressCount(selectedPrefix);
    const headroomCount = Math.max(0, totalAddresses - totalUniqueHostsAcrossNetworks);
    const headroomPercent = totalAddresses > 0 ? (headroomCount / totalAddresses) * 100 : 0;
    return {
      summary: `${intToIpv4(widenedNetwork)}/${selectedPrefix}`,
      totalAddresses,
      headroomCount,
      headroomPercent,
    };
  }, [tightestSummary, selectedPrefix, totalUniqueHostsAcrossNetworks]);

  // Design agent: The "Impact" list — for every entry, how much wider the summary would need to
  // become without it, so admins can see at a glance which address is forcing the wide summary.
  // Developer agent: deltaBits compares the tightest prefix to the prefix computed with that one
  // entry excluded; the single largest positive delta earns the "outlier" badge.
  const impactList = useMemo(() => {
    if (indexedValidEntries.length < 2 || tightestPrefix === null) {
      return [];
    }
    const rows = indexedValidEntries.map((entry, localIndex) => {
      const without = summaryWithout(validParsedList, localIndex);
      const withoutPrefix = without ? without.prefix : tightestPrefix;
      const deltaBits = withoutPrefix - tightestPrefix;
      return {
        entryNumber: entry.index + 1,
        cidrLabel: `${intToIpv4(entry.analysis.parsed.network)}/${entry.analysis.parsed.prefix}`,
        withoutPrefix,
        deltaBits,
      };
    });
    const maxDelta = rows.reduce((max, row) => Math.max(max, row.deltaBits), 0);
    return rows.map((row) => ({
      ...row,
      isFlagged: row.deltaBits >= 1,
      isOutlier: maxDelta >= 1 && row.deltaBits === maxDelta,
    }));
  }, [indexedValidEntries, validParsedList, tightestPrefix]);

  // --- Spec 005: Overlap origin ----------------------------------------------------------------

  // Design agent: Names every overlapping pair (or duplicate) of entries so the summary card can
  // point straight at the fields involved instead of only reporting an aggregate count.
  // Developer agent: findOverlaps is fed the full analyses array (nulls for empty/invalid rows
  // included) so the returned `a`/`b` positions already match the visible 1-based entry numbers.
  const overlapPairs = useMemo(() => {
    const parsedByOriginalIndex = analyses.map((analysis) => (analysis.parsed ? analysis.parsed : null));
    return findOverlaps(parsedByOriginalIndex);
  }, [analyses]);

  // Design agent: Hover handlers so pointing at a pair highlights both corresponding input fields
  // elsewhere in the inspector; both callbacks are optional so the card still renders standalone.
  const handlePairMouseEnter = (pair) => {
    if (typeof onHighlightEntries === 'function') {
      onHighlightEntries([pair.a, pair.b]);
    }
  };

  const handlePairMouseLeave = () => {
    if (typeof onClearHighlightedEntries === 'function') {
      onClearHighlightedEntries();
    }
  };

  if (validAnalyses.length < 2) {
    return null;
  }

  return (
    <div className="result-card overall-summary-card" id="overall-summary-card">
      <div className="insight-card-header" id="overall-summary-header">
        <div className="insight-card-header-text" id="overall-summary-header-text">
          <span className="insight-card-label" id="overall-summary-label">
            Overall Summary
          </span>
          <span className="insight-card-summary" id="overall-summary-sublabel">
            Aggregate insights for all valid networks
          </span>
        </div>
      </div>
      <div className="insight-card-body" id="overall-summary-body">
        <div className="summary-section summary-section-route-explorer" id="summary-section-route">
          <h4 className="section-title" id="section-title-route">Route Summary</h4>
          {/* Design agent: Highlights the combined route using the emphasis variant for quick scanning. */}
          {/* Developer agent: Reuses summary-stat styles to keep structure consistent across sections. */}
          <div className="summary-stat summary-stat-emphasis" id="summary-stat-route">
            <span className="summary-stat-label" id="summary-stat-label-route">Aggregate CIDR</span>
            <span
              className={routeExplorer ? 'summary-stat-value' : 'summary-stat-value summary-stat-value-unavailable'}
              id="summary-stat-value-route"
            >
              {routeExplorer ? routeExplorer.summary : 'Not Available'}
            </span>
          </div>
          {routeExplorer && tightestPrefix !== null && (
            <div className="route-gauge" id="route-gauge">
              {/* Design agent: A single slider is the most direct way to let an admin "try on" a
                  wider summary while always seeing the tightest option as the anchor point. */}
              {/* Developer agent: min/max are clamped above so the native range input never sees
                  an inverted or degenerate bound. */}
              <div className="route-gauge-header" id="route-gauge-header">
                <label className="route-gauge-label" htmlFor="route-gauge-input" id="route-gauge-label">
                  Widen summary
                  <span
                    className="route-gauge-tooltip-icon"
                    id="route-gauge-tooltip-icon"
                    title="A shorter prefix summarises more compactly (less pollution in routing tables) but claims more addresses you may not own. Use the slider to see the trade-off."
                  >
                    ⓘ
                  </span>
                </label>
                <button
                  type="button"
                  className="route-gauge-reset-button"
                  id="route-gauge-reset-button"
                  onClick={handleResetToTightest}
                  disabled={selectedPrefix === tightestPrefix}
                >
                  Tightest
                </button>
              </div>
              <input
                type="range"
                id="route-gauge-input"
                className="route-gauge-input"
                min={gaugeFloorPrefix}
                max={tightestPrefix}
                step={1}
                value={selectedPrefix}
                onChange={(event) => setSelectedPrefix(Number(event.target.value))}
              />
              <div className="route-gauge-scale" id="route-gauge-scale">
                <span id="route-gauge-scale-min">/{gaugeFloorPrefix} (widest)</span>
                <span id="route-gauge-scale-current">/{selectedPrefix}</span>
                <span id="route-gauge-scale-max">/{tightestPrefix} (tightest)</span>
              </div>
              <div className="route-gauge-stats" id="route-gauge-stats">
                <div className="summary-stat" id="summary-stat-gauge-total">
                  <span className="summary-stat-label" id="summary-stat-label-gauge-total">Total addresses</span>
                  <span className="summary-stat-value" id="summary-stat-value-gauge-total">
                    {routeExplorer.totalAddresses.toLocaleString()}
                  </span>
                </div>
                <div className="summary-stat" id="summary-stat-gauge-headroom">
                  <span className="summary-stat-label" id="summary-stat-label-gauge-headroom">Headroom</span>
                  <span className="summary-stat-value" id="summary-stat-value-gauge-headroom">
                    {routeExplorer.headroomCount.toLocaleString()} ({routeExplorer.headroomPercent.toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>
          )}
          {impactList.length > 0 && (
            <div className="impact-list" id="impact-list">
              <h5 className="impact-list-title" id="impact-list-title">Impact</h5>
              <ul className="impact-list-items" id="impact-list-items">
                {impactList.map((row) => (
                  <li
                    key={`impact-row-${row.entryNumber}`}
                    id={`impact-row-${row.entryNumber}`}
                    className={`impact-row${row.isFlagged ? ' impact-row--flagged' : ''}${row.isOutlier ? ' impact-row--outlier' : ''}`}
                  >
                    <span className="impact-row-label" id={`impact-row-label-${row.entryNumber}`}>
                      Entry #{row.entryNumber} ({row.cidrLabel})
                    </span>
                    <span className="impact-row-detail" id={`impact-row-detail-${row.entryNumber}`}>
                      /{row.withoutPrefix} without it
                      {row.deltaBits !== 0 && ` (${row.deltaBits > 0 ? '+' : ''}${row.deltaBits} bits)`}
                    </span>
                    {row.isOutlier && (
                      <span className="impact-row-badge" id={`impact-row-badge-${row.entryNumber}`}>
                        outlier
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="summary-section" id="summary-section-total">
          <h4 className="section-title" id="section-title-total">Total</h4>
          {/* Design agent: Presents aggregate counts with stacked labels for clarity. */}
          {/* Developer agent: Keeps values pre-formatted to simplify future localisation. */}
          <div className="summary-stat" id="summary-stat-total-size">
            <span className="summary-stat-label" id="summary-stat-label-total-size">Size (addresses)</span>
            <span className="summary-stat-value" id="summary-stat-value-total-size">
              {totalSizeOfAllNetworks.toLocaleString()}
            </span>
          </div>
          <div className="summary-stat" id="summary-stat-total-unique-hosts">
            <span className="summary-stat-label" id="summary-stat-label-total-unique-hosts">Unique Hosts</span>
            <span className="summary-stat-value" id="summary-stat-value-total-unique-hosts">
              {totalUniqueHostsAcrossNetworks.toLocaleString()}
            </span>
          </div>
        </div>
        <div className="summary-section" id="summary-section-overlap">
          <h4 className="section-title" id="section-title-overlap">Overlap</h4>
          {/* Design agent: Groups overlap metrics so users can compare counts effortlessly. */}
          {/* Developer agent: Avoids repeated inline styles by leaning on shared summary-stat classes. */}
          <div className="summary-stat" id="summary-stat-overlap-networks">
            <span className="summary-stat-label" id="summary-stat-label-overlap-networks">Overlapping Networks</span>
            <span className="summary-stat-value" id="summary-stat-value-overlap-networks">
              {totalOverlappingNetworks.toLocaleString()}
            </span>
          </div>
          <div className="summary-stat" id="summary-stat-overlap-hosts">
            <span className="summary-stat-label" id="summary-stat-label-overlap-hosts">Overlapping Hosts</span>
            <span className="summary-stat-value" id="summary-stat-value-overlap-hosts">
              {totalOverlappingHosts.toLocaleString()}
            </span>
          </div>
          {overlapPairs.length > 0 && (
            <div className="overlap-origin-list" id="overlap-origin-list">
              {/* Design agent: Names each overlapping pair by its visible entry number so an admin
                  never has to guess which two fields are colliding. */}
              {/* Developer agent: Hover handlers lift the two involved indices up to IpInspector,
                  which is responsible for outlining the matching input fields. */}
              <ul className="overlap-origin-items" id="overlap-origin-items">
                {overlapPairs.map((pair) => {
                  const entryNumberA = pair.a + 1;
                  const entryNumberB = pair.b + 1;
                  const analysisA = analyses[pair.a];
                  const analysisB = analyses[pair.b];
                  const labelA = `${intToIpv4(analysisA.parsed.network)}/${analysisA.parsed.prefix}`;
                  const labelB = `${intToIpv4(analysisB.parsed.network)}/${analysisB.parsed.prefix}`;
                  return (
                    <li
                      key={`overlap-pair-${pair.a}-${pair.b}`}
                      id={`overlap-pair-${pair.a}-${pair.b}`}
                      className={`overlap-origin-row${pair.isDuplicate ? ' overlap-origin-row--duplicate' : ''}`}
                      onMouseEnter={() => handlePairMouseEnter(pair)}
                      onMouseLeave={handlePairMouseLeave}
                    >
                      {pair.isDuplicate ? (
                        <span id={`overlap-pair-text-${pair.a}-${pair.b}`}>
                          Entry #{entryNumberA} ({labelA}) — duplicate of #{entryNumberB}
                        </span>
                      ) : (
                        <span id={`overlap-pair-text-${pair.a}-${pair.b}`}>
                          Entry #{entryNumberA} ({labelA}) ⟷ Entry #{entryNumberB} ({labelB}) — {pair.shared.toLocaleString()} shared addresses
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
        <div className="summary-section" id="summary-section-separated">
          <h4 className="section-title" id="section-title-separated">Separated</h4>
          {/* Design agent: Calls out gaps with the same metric language to reinforce pattern recognition. */}
          {/* Developer agent: Leaves room for additional statistics without revisiting layout logic. */}
          <div className="summary-stat" id="summary-stat-separated-networks">
            <span className="summary-stat-label" id="summary-stat-label-separated-networks">Unmentioned Networks</span>
            <span className="summary-stat-value" id="summary-stat-value-separated-networks">
              {unmentionedNetworksCount.toLocaleString()}
            </span>
          </div>
          <div className="summary-stat" id="summary-stat-separated-hosts">
            <span className="summary-stat-label" id="summary-stat-label-separated-hosts">Unmentioned Hosts</span>
            <span className="summary-stat-value" id="summary-stat-value-separated-hosts">
              {unmentionedHostsCount.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OverallSummaryCard;
